/**
 * Service Worker Bootstrap
 * Handles SW registration, message listening, and push notification integration
 */
import soundPlayer from './soundPlayer';

class ServiceWorkerBootstrap {
  constructor() {
    this.registration = null;
    this.isInitialized = false;
    this.messageListeners = new Map();
    this.onQuoteRefresh = null;
  }

  /**
   * Initialize service worker and message listeners
   */
  async initialize(options = {}) {
    if (this.isInitialized) {
      return this.registration;
    }

    this.onQuoteRefresh = options.onQuoteRefresh || null;

    try {
      // Register service worker
      if ('serviceWorker' in navigator) {
        this.registration = await navigator.serviceWorker.register('/webhook-sw.js', {
          updateViaCache: 'none'
        });

        console.log('🔧 Service Worker registered successfully');

        // Wait for SW to be ready
        await navigator.serviceWorker.ready;

        // Setup message listener
        this.setupMessageListener();

        // Check for updates
        this.setupUpdateListener();

        // Initialize sound player after user gesture
        this.setupSoundPrimer();

        // Auto-subscribe to push notifications
        this.autoSubscribeToPush();

        this.isInitialized = true;
        console.log('✅ Service Worker bootstrap initialized');

        return this.registration;
      } else {
        console.warn('Service Workers not supported');
        return null;
      }
    } catch (error) {
      console.error('Failed to initialize Service Worker:', error);
      throw error;
    }
  }

  /**
   * Auto-subscribe to push notifications on login
   */
  async autoSubscribeToPush() {
    try {
      // Check if push notifications are supported
      if (!('PushManager' in window)) {
        console.log('❌ Push notifications not supported');
        return;
      }

      // Get user ID from auth context (stored in cookie)
      const userId = await this.getUserId();
      if (!userId) {
        console.log('⏭️ No user logged in, skipping push subscription');
        return;
      }

      // Check if already subscribed
      const existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('✅ Already subscribed to push notifications');
        console.log('📤 Sending existing subscription to push server...');
        // Still send to server in case it's not registered there
        await this.sendSubscriptionToServer(userId, existingSubscription);
        console.log('✅ Existing subscription confirmed with push server');
        return;
      }

      // Request notification permission if needed
      if (Notification.permission === 'default') {
        console.log('🔔 Requesting notification permission...');
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log('❌ Notification permission denied');
          return;
        }
      } else if (Notification.permission !== 'granted') {
        console.log('❌ Notification permission not granted');
        return;
      }

      // Get VAPID public key from push server
      const pushServerUrl = process.env.REACT_APP_PUSH_SERVER_URL || window.location.origin;
      const response = await fetch(`${pushServerUrl}/webhook/vapid-public-key`);
      const { key } = await response.json();

      // Subscribe to push
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key
      });

      console.log('✅ Subscribed to push notifications');

      // Send subscription to server
      await this.sendSubscriptionToServer(userId, subscription);

      console.log('✅ Push subscription saved to server');
    } catch (error) {
      console.error('❌ Failed to auto-subscribe to push:', error);
    }
  }

  /**
   * Get user ID from auth cookie
   */
  async getUserId() {
    try {
      // Try to get from cookie
      const cookies = document.cookie.split(';');
      const tokenCookie = cookies.find(c => c.trim().startsWith('quotes_token='));
      if (!tokenCookie) return null;

      const token = tokenCookie.split('=')[1];
      if (!token) return null;

      // Decode JWT to get user_id (simple base64 decode of payload)
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.user_id ? String(decoded.user_id) : null;
    } catch (error) {
      console.error('Failed to get user ID:', error);
      return null;
    }
  }

  /**
   * Send subscription to push server
   */
  async sendSubscriptionToServer(userId, subscription) {
    const pushServerUrl = process.env.REACT_APP_PUSH_SERVER_URL || window.location.origin;
    const subscribeUrl = `${pushServerUrl}/webhook/subscribe`;

    console.log(`📡 Sending subscription to push server: ${subscribeUrl}`);
    console.log(`👤 User ID: ${userId}`);
    console.log(`🔗 Endpoint: ${subscription.endpoint.substring(0, 50)}...`);

    const response = await fetch(subscribeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        user_id: userId,
        subscription: subscription.toJSON()
      })
    });

    console.log(`📡 Push server response: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      throw new Error(`Failed to save subscription: ${response.status}`);
    }
  }

  /**
   * Setup message listener for SW messages
   */
  setupMessageListener() {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.addEventListener('message', (event) => {
      console.log('📨 SW message received:', event.data);

      const { type, payload } = event.data;

      // Handle different message types
      switch (type) {
        case 'PUSH_NOTIFICATION_RECEIVED':
          this.handlePushNotification(payload);
          break;
        
        case 'WEBHOOK_QUOTE_REQUEST':
          this.handleWebhookQuoteRequest(payload);
          break;
        
        case 'WEBHOOK_QUOTE_UPDATE':
          this.handleWebhookQuoteUpdate(payload);
          break;
        
        case 'WEBHOOK_USER_NOTIFICATION':
          this.handleWebhookUserNotification(payload);
          break;
        
        default:
          console.log('🔍 Unknown SW message type:', type, payload);
      }

      // Notify registered listeners
      this.notifyListeners(type, payload);
    });

    console.log('👂 SW message listener setup complete');
  }

  /**
   * Handle push notifications - play sound and refresh data
   */
  async handlePushNotification(payload) {
    console.log('🔔 Processing push notification:', payload);

    try {
      // Play notification sound
      if (payload.playSound) {
        console.log('🔊 Playing notification sound...');
        await soundPlayer.playNotificationSound();
      }

      // Handle message notifications
      if (payload.message_data) {
        console.log('💬 Processing message notification:', payload.message_data);

        // Dispatch custom event for message notifications
        window.dispatchEvent(new CustomEvent('messageNotification', {
          detail: {
            requestId: payload.message_data.request_id,
            lineId: payload.message_data.line_id,
            messageId: payload.message_data.message_id,
            isNote: payload.message_data.is_note,
            body: payload.message_data.body,
            author: payload.message_data.author
          }
        }));
      }

      // Refresh quotes data
      if (payload.refreshQuotes) {
        console.log('🔄 Refreshing quotes data...');

        if (this.onQuoteRefresh) {
          await this.onQuoteRefresh(payload);
        } else {
          // Fallback: dispatch custom event
          window.dispatchEvent(new CustomEvent('quoteDataRefresh', {
            detail: payload
          }));
        }
      }

      console.log('✅ Push notification processed successfully');

    } catch (error) {
      console.error('❌ Failed to process push notification:', error);
    }
  }

  /**
   * Handle webhook quote requests
   */
  async handleWebhookQuoteRequest(payload) {
    console.log('📝 Processing webhook quote request:', payload);
    
    // Dispatch event for components to listen to
    window.dispatchEvent(new CustomEvent('webhookQuoteRequest', {
      detail: payload
    }));
  }

  /**
   * Handle webhook quote updates
   */
  async handleWebhookQuoteUpdate(payload) {
    console.log('📝 Processing webhook quote update:', payload);
    
    // Dispatch event for components to listen to
    window.dispatchEvent(new CustomEvent('webhookQuoteUpdate', {
      detail: payload
    }));
  }

  /**
   * Handle webhook user notifications
   */
  async handleWebhookUserNotification(payload) {
    console.log('👤 Processing webhook user notification:', payload);
    
    // Play sound for user notifications
    try {
      await soundPlayer.playNotificationSound();
    } catch (error) {
      console.warn('Could not play user notification sound:', error);
    }
    
    // Dispatch event for components to listen to
    window.dispatchEvent(new CustomEvent('webhookUserNotification', {
      detail: payload
    }));
  }

  /**
   * Setup service worker update listener
   */
  setupUpdateListener() {
    if (!this.registration) return;

    this.registration.addEventListener('updatefound', () => {
      const newWorker = this.registration.installing;
      
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            console.log('🔄 New service worker available, reloading...');
            // Auto-reload for push notifications (critical functionality)
            window.location.reload();
          }
        }
      });
    });
  }

  /**
   * Setup sound priming - auto-initialize on user interaction
   */
  setupSoundPrimer() {
    // Sounds are enabled by default, auto-initialize on first user interaction
    const initSoundOnInteraction = async () => {
      try {
        if (!soundPlayer.isInitialized) {
          console.log('🔊 Auto-initializing sound player...');
          await soundPlayer.initialize();
          console.log('✅ Sound player auto-initialized successfully');
        }
      } catch (error) {
        console.warn('Failed to auto-initialize sound player:', error);
        // Only show button if auto-initialization fails
        this.showSoundEnableButton();
      }

      // Remove listeners after first interaction
      document.removeEventListener('click', initSoundOnInteraction);
      document.removeEventListener('keydown', initSoundOnInteraction);
      document.removeEventListener('touchstart', initSoundOnInteraction);
    };

    // Try to initialize on any user interaction
    document.addEventListener('click', initSoundOnInteraction, { once: true });
    document.addEventListener('keydown', initSoundOnInteraction, { once: true });
    document.addEventListener('touchstart', initSoundOnInteraction, { once: true });
  }

  /**
   * Show sound enable button (fallback for failed auto-initialization)
   */
  showSoundEnableButton() {
    if (!document.querySelector('[data-sound-primer]')) {
      const primer = soundPlayer.createSoundPrimer();
      primer.setAttribute('data-sound-primer', 'true');
      document.body.appendChild(primer);
    }
  }

  /**
   * Register message listener
   */
  addMessageListener(type, callback) {
    if (!this.messageListeners.has(type)) {
      this.messageListeners.set(type, []);
    }
    this.messageListeners.get(type).push(callback);
  }

  /**
   * Remove message listener
   */
  removeMessageListener(type, callback) {
    if (this.messageListeners.has(type)) {
      const listeners = this.messageListeners.get(type);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Notify registered listeners
   */
  notifyListeners(type, payload) {
    if (this.messageListeners.has(type)) {
      this.messageListeners.get(type).forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error('Error in message listener:', error);
        }
      });
    }
  }

  /**
   * Send message to service worker
   */
  async sendMessage(message) {
    if (!this.registration || !this.registration.active) {
      console.warn('Cannot send message - no active service worker');
      return false;
    }

    try {
      this.registration.active.postMessage(message);
      return true;
    } catch (error) {
      console.error('Failed to send message to SW:', error);
      return false;
    }
  }

  /**
   * Get current status for debugging
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      hasRegistration: !!this.registration,
      swState: this.registration?.active?.state,
      soundStatus: soundPlayer.getStatus(),
      listenerTypes: Array.from(this.messageListeners.keys())
    };
  }
}

// Export singleton
const serviceWorkerBootstrap = new ServiceWorkerBootstrap();
export default serviceWorkerBootstrap;